import type { ColumnEntry } from "@/v2/framework/column/types";
import type { DynamicColumnSlot } from "@/v2/framework/view/types";
import { FieldType, ColumnIconType } from "@/v2/framework/column/types";
import { t } from "@/services/i18n";
import { getSupplementalsByEntity } from "@/services/supplementals";
import { teamStatusOptions } from "../shared";

type TeamGridRow = {
  sportCode?: string;
  status?: number;
  feeds?: { name: string }[];
  supplementalNames?: { supplementalNameTypeId: string | number; name?: string }[];
};

export const columns: ColumnEntry[] = [
  { key: "id", defaultVisibility: true, title: "table_columns.id", fieldType: FieldType.TextCell },
  {
    key: "syntheticId",
    defaultVisibility: false,
    title: "table_columns.synthetic_id",
    fieldType: FieldType.TextCell,
  },
  {
    key: "sportName",
    defaultVisibility: true,
    title: "table_columns.sport",
    fieldType: FieldType.TextCell,
    icon: {
      type: ColumnIconType.Prepend,
      formatter: (row: unknown) => ({ code: `${(row as TeamGridRow).sportCode ?? ""}` }),
    },
  },
  {
    key: "sourceTenantName",
    defaultVisibility: false,
    title: "table_columns.source_tenant_name",
    fieldType: FieldType.TextCell,
  },
  {
    key: "name",
    defaultVisibility: true,
    title: "table_columns.name",
    fieldType: FieldType.InputCell,
  },
  {
    key: "sourceName",
    defaultVisibility: true,
    title: "table_columns.origin_name",
    fieldType: FieldType.TextCell,
  },
  {
    key: "translatedName",
    defaultVisibility: false,
    title: "table_columns.translated_name",
    fieldType: FieldType.TextCell,
  },
  {
    key: "status",
    defaultVisibility: true,
    title: "table_columns.status",
    fieldType: FieldType.TextCell,
    valueFormatter: (row: unknown) => {
      const status = teamStatusOptions.find((option) => option.value === (row as TeamGridRow).status);
      return t(status?.title ?? "");
    },
    icon: {
      type: ColumnIconType.Prepend,
      formatter: (row: unknown) => {
        const status = teamStatusOptions.find(
          (option) => option.value === (row as TeamGridRow).status,
        );
        return { code: status?.icon?.code ?? "", color: status?.icon?.color ?? "" };
      },
    },
  },
  {
    key: "feeds",
    defaultVisibility: false,
    title: "table_columns.feeds",
    fieldType: FieldType.ArrayCell,
    valueFormatter: (row: unknown) =>
      ((row as TeamGridRow).feeds ?? []).map((feed) => feed.name?.split(" - ")[0] ?? ""),
  },
  {
    key: "info",
    defaultVisibility: false,
    title: "table_columns.info",
    fieldType: FieldType.TextCell,
  },
  {
    key: "gridChrome",
    defaultVisibility: true,
    configurableVisibility: false,
    title: "",
    fieldType: FieldType.ActionsCell,
    sticky: true,
    columnsPanelTitle: "common.labels.column_view",
    headerActions: [{ icon: "table-d", label: "Column view" }],
    rowActions: [
      { icon: "edit-ng", label: "Edit", onClick: (ctx) => ctx.openDialog("edit") },
      { icon: "trash-ng", label: "Delete", onClick: (ctx) => ctx.openDialog("delete") },
    ],
  },
];

export const dynamicColumnSlots: DynamicColumnSlot[] = [
  {
    anchor: "name",
    createColumns: () =>
      getSupplementalsByEntity("TEAM").map((item) => ({
        key: `supplementalName_${item.id}_name`,
        title: `table_columns.${item.name.toLowerCase()}`,
        fieldType: FieldType.InputCell,
        defaultVisibility: true,
        valueFormatter: (row: unknown) =>
          (row as TeamGridRow).supplementalNames?.find(
            (entry) => String(entry.supplementalNameTypeId) === String(item.id),
          )?.name ?? "",
        payloadOutFormatter: (value: unknown) => ({
          supplementalNames: [{ supplementalNameTypeId: item.id, name: value }],
        }),
      })),
  },
];
